import appState from 'snippets/search-bar/@store'
import { createContext } from 'preact'
import { useContext, useMemo } from 'preact/hooks'
import debounce from 'lodash.debounce'
import SearchDropdown from 'snippets/search-dropdown/search-dropdown'
import { selectAll, removeClass, addClass } from 'helpers/dom'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'

let devtools
if (process.env.NODE_ENV === 'development') {
  devtools = require('preact/devtools')
}

const selectors = {
  searchBarClass: '.js-search-bar',
  searchDropDownClass: '.js-search-dropdown',
  searchOpenedClass: 'is-search-opened'
}

const AppState = createContext()

function SearchBar(props) {
  return (
    <AppState.Provider value={appState()}>
      <App slot={props} />
    </AppState.Provider>
  )
}

function App(props) {
  const state = useContext(AppState)

  const originalQuery = useMemo(() => state.originalQuery.value, [state.originalQuery.value])
  const isOpenDropdown = useMemo(() => state.isOpenDropdown.value, [state.isOpenDropdown.value])
  const isMobile = useMemo(() => state.isMobile.value, [state.isMobile.value])

  const searchUrl = useMemo(() => {
    let newUrl = `/search?q=${encodeURIComponent(state.originalQuery.value)}`
    if (state.searchValue.value && state.searchValue.value !== state.originalQuery.value) {
      newUrl = `/search?q=${encodeURIComponent(state.searchValue.value)}&oq=${encodeURIComponent(state.originalQuery.value)}&spell=1`
    }
    if (state.searchValue.value && state.correctedQuery.value && state.correctedQuery.value !== state.searchValue.value) {
      newUrl = `/search?q=${encodeURIComponent(state.correctedQuery.value)}&oq=${encodeURIComponent(state.searchValue.value)}&spell=1`
    }
    return newUrl
  }, [state.originalQuery.value, state.searchValue.value, state.correctedQuery.value])

  const searchBarEl = selectAll(selectors.searchBarClass)

  const search = debounce((e) => {
    const currentValue = e.target.value
    state.setOriginalQuery(currentValue)
    if (currentValue.length) {
      state.getSuggestion(currentValue)
    } else {
      state.closeDropdown()
    }
  }, 500)

  const closeSearch = () => {
    state.setOriginalQuery(null)
    state.closeDropdown()
    clearAllBodyScrollLocks()
    searchBarEl.forEach(el => {
      removeClass(selectors.searchOpenedClass, el)
    })
  }

  const onSubmit = (e) => {
    e.preventDefault()
    window.location.href = searchUrl
  }

  const onSearch = (e) => {
    if (e.keyCode === 13) {
      const currentValue = e.target.value
      if (!originalQuery && currentValue) {
        e.preventDefault()
        window.location.href = `/search?q=${encodeURIComponent(currentValue)}`
      }
    }
  }

  const onOpen = (e) => {
    setTimeout(() => {
      e.target.nextElementSibling.focus()
    }, 300)
    searchBarEl.forEach(el => {
      addClass(selectors.searchOpenedClass, el)
      isMobile && disableBodyScroll(el)
    })
  }

  return (
    <div className='top-[var(--header-offset-top)] left-0 z-10 flex flex-col w-full max-h-[calc(100%_-_var(--header-offset-top))] [.is-search-opened_&]:md-max:fixed bg-white md:top-0 [.is-sticky_&]:xl:px-6 search-bar'>
      <form action={searchUrl} className="[.is-search-opened_&]:p-4 z-20 flex-items-center md:px-4 md:py-2 md:rounded-full md:bg-grey-100 search-bar__form" onSubmit={onSubmit}>
        <button type="submit" className="text-grey-900" aria-label="Search">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 20 20">
            <path stroke="currentColor" stroke-miterlimit="10" stroke-width="1.8" d="M8.636 2.5a6.136 6.136 0 1 0 0 12.273 6.136 6.136 0 0 0 0-12.273Z" />
            <path stroke="currentColor" stroke-linecap="round" stroke-miterlimit="10" stroke-width="1.8" d="m13.214 13.215 4.286 4.286" />
          </svg>
        </button>
        <label for="searchbar" class="absolute inset-0 bg-white [.is-search-opened_&]:!hidden md:hidden [.is-sticky_&]:xl:block cursor-pointer" onClick={onOpen}>
          <svg class="icon pointer-events-none" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="currentColor" stroke-miterlimit="10" stroke-width="1.8" d="M8.636 2.5a6.136 6.136 0 1 0 0 12.273 6.136 6.136 0 0 0 0-12.273Z"/>
            <path stroke="currentColor" stroke-linecap="round" stroke-miterlimit="10" stroke-width="1.8" d="m13.214 13.215 4.286 4.286"/>
          </svg>
        </label>
        <input
          id="searchbar"
          type="text"
          className="flex-1 p-0 mx-2 bg-transparent border-none focus:outline-none focus:shadow-0 text-grey-700 placeholder-grey-700 js-search-bar-input"
          placeholder={props.slot.placeholder}
          onInput={search}
          onkeydown={onSearch}
        />
        <button type="button" className={`hidden md-max:block ${isOpenDropdown && '!block'} search-bar__close`} onClick={closeSearch} aria-label="search close">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 16 16" class="w-4 h-4 pointer-events-none">
            <path
              id="icon-close-outline"
              d="M13.333 13.333 2.667 2.667M13.333 2.667 2.667 13.333"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </form>
      {isOpenDropdown && <SearchDropdown state={state} closeSearch={closeSearch}></SearchDropdown>}
    </div>
  )
}

export default SearchBar
